/**
 * Friend invite rules shared by the app and Netlify.
 * CommonJS only. No object spread, and no Node builtins:
 * the iOS bundle loads this file.
 * Friend-facing copy never includes a homeowner count.
 */

const LINK_DAYS = 30;
const PENDING_CAP = 10;
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{20,128}$/;

const DECLINE_SUBJECT = 'About your Pacarion request';
const START_OWN_ACCOUNT_LABEL = 'Start my own account';
const AGENT_PAUSE_MESSAGE = 'New homeowners are paused. Existing homeowners keep access.';
const ALREADY_SPONSOR_MESSAGE = 'This home already has a sponsor.';
const ALREADY_CONNECTED_MESSAGE = "You're already connected.";
const SELF_INVITE_MESSAGE = "You can't use your own invite link.";
const PENDING_CAP_MESSAGE = 'This invite already has several open requests. Try again later.';
const INVITE_INVALID_MESSAGE = "This invite link isn't valid.";

function validToken(token) {
  return TOKEN_PATTERN.test(String(token || ''));
}

function appBase(env) {
  const configured = String((env && env.APP_BASE_URL) || '').trim().replace(/\/$/, '');
  if (configured) return configured;
  return 'https://app.pacarion.com';
}

function friendJoinUrl(token, base) {
  const root = String(base || 'https://app.pacarion.com').replace(/\/$/, '');
  return `${root}/join?ref=${encodeURIComponent(String(token || ''))}`;
}

function planUrl(base) {
  const root = String(base || 'https://app.pacarion.com').replace(/\/$/, '');
  return `${root}/homeowner/plan`;
}

function friendJoinFromLocation(pathname, search) {
  const path = String(pathname || '').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  if (path !== '/join') return '';
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const ref = String(params.get('ref') || '').trim();
  return validToken(ref) ? ref : '';
}

function linkExpiry(now) {
  const start = now instanceof Date ? now.getTime() : Number(now) || Date.now();
  return new Date(start + LINK_DAYS * 24 * 60 * 60 * 1000).toISOString();
}

function pendingRoom(count) {
  const used = Number(count) || 0;
  return used < PENDING_CAP;
}

function acceptBlocked(used, limit) {
  const count = Number(used) || 0;
  const cap = Number(limit);
  if (!Number.isFinite(cap) || cap <= 0) return true;
  return count >= cap + Math.floor(cap / 10);
}

function sameEmail(left, right) {
  return String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();
}

function firstName(name, fallback) {
  const cleaned = String(name || '').replace(/\s+/g, ' ').trim();
  const safe = fallback || 'Your sponsor';
  if (!cleaned || cleaned.toLowerCase() === safe.toLowerCase()) return safe;
  const space = cleaned.indexOf(' ');
  return space === -1 ? cleaned : cleaned.slice(0, space);
}

function agentCanSponsor(profile) {
  if (!profile) return false;
  if (profile.role === 'super_admin') return true;
  const status = profile.subscription_status || '';
  return status === 'trialing' || status === 'active';
}

function publicLinkView(link) {
  const source = link || {};
  const sponsor = agentCanSponsor(source.agent) || source.agentActive === true;
  const agentKnown = !!(source.agent || source.agentFirstName);
  const open = sponsor && agentKnown;
  return {
    inviterFirstName: firstName(source.inviterFirstName, 'A homeowner'),
    agentFirstName: firstName(source.agentFirstName, 'Your sponsor'),
    agentActive: open,
    choices: open ? ['sponsor', 'own'] : ['own'],
  };
}

function choiceAllowed(view, choice) {
  const choices = (view && view.choices) || [];
  return choices.indexOf(choice) !== -1;
}

function agentContact(row) {
  const source = row || {};
  return {
    id: source.id || '',
    name: source.name || '',
    email: source.email || '',
    phone: source.phone || '',
  };
}

function decisionHttp(code) {
  if (code === 'limit_reached') return { status: 409, error: AGENT_PAUSE_MESSAGE };
  if (code === 'not_found') return { status: 404, error: 'That request is no longer pending.' };
  if (code === 'expired') return { status: 409, error: 'That invite link has expired.' };
  if (code === 'subscription_inactive') return { status: 403, error: 'Your subscription is not active.' };
  if (code === 'already_decided') return { status: 409, error: 'That request was already decided.' };
  if (code === 'already_sponsored') return { status: 409, error: ALREADY_SPONSOR_MESSAGE };
  if (code === 'unavailable') return { status: 409, error: "That home can't be added." };
  return { status: 400, error: 'Could not update that request.' };
}

function publicDecision(result) {
  const source = result || {};
  if (source.ok === true) {
    return { ok: true, outcome: source.outcome === 'declined' ? 'declined' : 'accepted' };
  }
  const code = String(source.code || 'error');
  const http = decisionHttp(code);
  return { ok: false, code, error: http.error };
}

function declineBody(agentFirst, options) {
  const who = firstName(agentFirst, 'Your sponsor');
  const limit = `${who} wasn't able to add you this time. Each sponsor's plan includes a limited number of homeowner spots.`;
  if (options && options.ownAccount === false) return limit;
  return `${limit} You can still keep everything you've set up by starting your own Pacarion account.`;
}

function waitingCopy(agentFirst) {
  return `Waiting for ${firstName(agentFirst, 'Your sponsor')} to accept.`;
}

function friendNotice(kind, input) {
  const source = input || {};
  const who = firstName(source.agentFirstName, 'Your sponsor');
  if (kind === 'declined') {
    return {
      subject: DECLINE_SUBJECT,
      paragraphs: [declineBody(who)],
      button: START_OWN_ACCOUNT_LABEL,
    };
  }
  if (kind === 'accepted') {
    return {
      subject: 'Your Pacarion home is connected',
      paragraphs: [
        `${who} accepted your request. Your home is on their plan while they keep their subscription.`,
      ],
      button: '',
    };
  }
  const friend = firstName(source.friendName, 'A homeowner');
  return {
    subject: `${friend} wants to join your homeowners`,
    paragraphs: [
      `${friend} asked to use your plan. Accept or decline in Pacarion. You will see their name, email, and phone only.`,
    ],
    button: '',
  };
}

function missingRelation(error) {
  const code = error && error.code;
  const text = String((error && (error.message || error.details)) || '');
  return code === '42P01' || code === 'PGRST205' || /does not exist|schema cache/i.test(text);
}

module.exports = {
  LINK_DAYS,
  PENDING_CAP,
  DECLINE_SUBJECT,
  START_OWN_ACCOUNT_LABEL,
  AGENT_PAUSE_MESSAGE,
  ALREADY_SPONSOR_MESSAGE,
  ALREADY_CONNECTED_MESSAGE,
  SELF_INVITE_MESSAGE,
  PENDING_CAP_MESSAGE,
  INVITE_INVALID_MESSAGE,
  validToken,
  appBase,
  friendJoinUrl,
  planUrl,
  friendJoinFromLocation,
  linkExpiry,
  pendingRoom,
  acceptBlocked,
  sameEmail,
  firstName,
  agentCanSponsor,
  publicLinkView,
  choiceAllowed,
  agentContact,
  decisionHttp,
  publicDecision,
  declineBody,
  waitingCopy,
  friendNotice,
  missingRelation,
};
